import { useEffect, useMemo, useRef, useState } from 'react';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ErroCarregamento, { extrairMensagemErro } from '../../components/common/ErroCarregamento';
import Pill from '../../components/common/Pill';
import CheckboxCascata, { estadoSelecao } from '../../components/common/CheckboxCascata';
import { requisitosDaSubsecao } from '../../utils/catalogo';
import { HOSPITALAR_LABELS } from '../../utils/acreditacaoOpcoes';
import * as catalogoApi from '../../api/catalogo';
import * as hospitaisApi from '../../api/hospitais';

const HOSPITALAR_TONE = { obrigatorio: 'dark', aplicavel: 'neutral', nao_aplicavel: 'outline' };

export default function AbaEscopo({ idHospital }) {
  const [secoes, setSecoes] = useState(null);
  const [escopoOriginal, setEscopoOriginal] = useState(null);
  const [alteracoes, setAlteracoes] = useState({});
  const [secoesAbertas, setSecoesAbertas] = useState(new Set());
  const [salvando, setSalvando] = useState(false);
  const [salvo, setSalvo] = useState(false);
  const [erroSalvar, setErroSalvar] = useState('');
  const [erroCarregar, setErroCarregar] = useState('');
  const [filtroCore, setFiltroCore] = useState('todos'); // 'todos' | 'core'
  const [busca, setBusca] = useState('');
  const requisicaoAtual = useRef(0);
  const buscaAtiva = busca.trim().length >= 2;

  const passaBusca = (r) => !buscaAtiva || `${r.codigo} ${r.texto}`.toLowerCase().includes(busca.trim().toLowerCase());

  const carregar = async () => {
    const minhaRequisicao = ++requisicaoAtual.current;
    const [resCatalogo, resEscopo] = await Promise.all([
      catalogoApi.listarHierarquia(),
      hospitaisApi.obterEscopo(idHospital),
    ]);
    if (requisicaoAtual.current !== minhaRequisicao) return; // resposta obsoleta (hospital já trocou)
    setSecoes(resCatalogo.data);
    setEscopoOriginal(resEscopo.data);
    setAlteracoes({});
  };

  const carregarInicial = async () => {
    setErroCarregar('');
    try {
      await carregar();
    } catch (err) {
      setErroCarregar(extrairMensagemErro(err, 'Não foi possível carregar o escopo.'));
    }
  };

  useEffect(() => {
    carregarInicial();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idHospital]);

  const ativoOriginalPorId = useMemo(() => {
    const mapa = new Map();
    (escopoOriginal || []).forEach((e) => mapa.set(e.id_requisito, e.ativo));
    return mapa;
  }, [escopoOriginal]);

  const todosRequisitos = useMemo(() => {
    if (!secoes) return [];
    return secoes.flatMap((secao) =>
      secao.subsecoes.flatMap((sub) => requisitosDaSubsecao(sub).map((r) => ({ ...r, hospitalar: sub.hospitalar })))
    );
  }, [secoes]);

  if (!secoes || !escopoOriginal) {
    return erroCarregar ? <ErroCarregamento mensagem={erroCarregar} onRetry={carregarInicial} /> : <LoadingSpinner />;
  }

  const ativoAtual = (idRequisito) =>
    idRequisito in alteracoes ? alteracoes[idRequisito] : ativoOriginalPorId.get(idRequisito) ?? false;

  const toggleRequisito = (idRequisito) => {
    setSalvo(false);
    setErroSalvar('');
    setAlteracoes((atual) => ({ ...atual, [idRequisito]: !ativoAtual(idRequisito) }));
  };

  // Marca/desmarca de uma vez todos os requisitos de um bloco (Seção ou Subseção) — pensado
  // pra desmarcar rápido uma Subseção inteira que não se aplica a este hospital, sem precisar
  // clicar requisito por requisito.
  const alternarBloco = (ids) => {
    setSalvo(false);
    setErroSalvar('');
    const todosAtivos = ids.every((id) => ativoAtual(id));
    setAlteracoes((atual) => {
      const novo = { ...atual };
      ids.forEach((id) => {
        novo[id] = !todosAtivos;
      });
      return novo;
    });
  };

  const toggleSecao = (id) =>
    setSecoesAbertas((s) => {
      const novo = new Set(s);
      novo.has(id) ? novo.delete(id) : novo.add(id);
      return novo;
    });

  const marcarTodos = () => {
    if (!window.confirm(`Marcar todos os ${todosRequisitos.length} requisitos como ativos no escopo deste hospital?`)) return;
    setSalvo(false);
    setErroSalvar('');
    const novo = {};
    todosRequisitos.forEach((r) => {
      novo[r.id_requisito] = true;
    });
    setAlteracoes(novo);
  };

  const desmarcarTodos = () => {
    if (!window.confirm(`Desmarcar todos os ${todosRequisitos.length} requisitos do escopo deste hospital?`)) return;
    setSalvo(false);
    setErroSalvar('');
    const novo = {};
    todosRequisitos.forEach((r) => {
      novo[r.id_requisito] = false;
    });
    setAlteracoes(novo);
  };

  // Os três presets abaixo substituem o escopo inteiro (marcam o critério e desmarcam o
  // resto), em vez de só ligar/desligar um subconjunto — mais previsível como ponto de partida.
  const aplicarPreset = (mensagem, condicao) => {
    const qtd = todosRequisitos.filter(condicao).length;
    if (!window.confirm(`${mensagem} (${qtd} requisito(s) ficarão ativos, o restante será desmarcado)?`)) return;
    setSalvo(false);
    setErroSalvar('');
    const novo = {};
    todosRequisitos.forEach((r) => {
      novo[r.id_requisito] = condicao(r);
    });
    setAlteracoes(novo);
  };

  const marcarApenasCore = () => aplicarPreset('Marcar apenas os requisitos CORE como ativos', (r) => r.core);

  const marcarApenasObrigatorios = () =>
    aplicarPreset('Marcar apenas os requisitos de subseções obrigatórias como ativos', (r) => r.hospitalar === 'obrigatorio');

  const marcarCoreObrigatorios = () =>
    aplicarPreset(
      'Marcar apenas os requisitos CORE de subseções obrigatórias como ativos',
      (r) => r.core && r.hospitalar === 'obrigatorio'
    );

  const qtdAlteracoes = Object.keys(alteracoes).length;

  const salvar = async () => {
    setSalvando(true);
    setErroSalvar('');
    try {
      const itens = Object.entries(alteracoes).map(([idRequisito, ativo]) => ({ id_requisito: idRequisito, ativo }));
      await hospitaisApi.atualizarEscopo(idHospital, itens);
    } catch (err) {
      setErroSalvar(err?.response?.data?.detail || 'Não foi possível salvar o escopo. Tente novamente.');
      setSalvando(false);
      return;
    }
    // o escopo já foi salvo — se a releitura abaixo falhar, não é um erro de salvar
    setSalvo(true);
    try {
      await carregar();
    } catch {
      // mantém os dados locais; a próxima ação do usuário tenta recarregar de novo
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div>
      <div className="d-flex align-items-start justify-content-between flex-wrap gap-3 mb-3">
        <p style={{ fontSize: 13, color: 'var(--am-cinza-texto-2)', margin: 0, maxWidth: 480 }}>
          Marque quais requisitos do catálogo este hospital efetivamente avalia. O Mapa de
          Conformidade e os gráficos consideram só os requisitos ativos aqui. Requisitos{' '}
          <strong style={{ color: 'var(--am-link)' }}>CORE</strong> aparecem destacados.
        </p>
        <div className="d-flex flex-column align-items-end gap-1">
          <div className="d-flex align-items-center gap-2">
            {salvo && <span style={{ fontSize: 13, color: 'var(--am-sucesso)' }}>Salvo!</span>}
            <button className="am-btn am-btn-dark" disabled={qtdAlteracoes === 0 || salvando} onClick={salvar}>
              {salvando ? 'Salvando...' : `Salvar alterações${qtdAlteracoes ? ` (${qtdAlteracoes})` : ''}`}
            </button>
          </div>
          {erroSalvar && <div className="am-field__error">{erroSalvar}</div>}
        </div>
      </div>

      <label className="am-field" style={{ maxWidth: 420, marginBottom: 14 }}>
        <div className="am-field__label-row">
          <span className="am-field__label">Buscar requisito</span>
        </div>
        <input
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          placeholder="Código ou texto do requisito..."
        />
      </label>

      <div className="filtros-painel mb-3">
        <div className="filtros-painel__linha">
          <span className="filtros-painel__label">Escopo</span>
          <div className="filter-tabs">
            <button
              type="button"
              className={`filter-tab${filtroCore === 'todos' ? ' is-active' : ''}`}
              onClick={() => setFiltroCore('todos')}
            >
              Todos
            </button>
            <button
              type="button"
              className={`filter-tab${filtroCore === 'core' ? ' is-active' : ''}`}
              onClick={() => setFiltroCore('core')}
            >
              Somente CORE
            </button>
          </div>
        </div>
        <div className="filtros-painel__linha">
          <span className="filtros-painel__label">Ações</span>
          <div className="d-flex gap-2 flex-wrap">
            <button type="button" className="am-btn am-btn-outline" onClick={marcarTodos}>
              <span className="ms">check_box</span>
              Marcar todos
            </button>
            <button type="button" className="am-btn am-btn-outline" onClick={desmarcarTodos}>
              <span className="ms">check_box_outline_blank</span>
              Desmarcar todos
            </button>
            <button type="button" className="am-btn am-btn-outline" onClick={marcarApenasCore}>
              <span className="ms">verified</span>
              Marcar apenas CORE
            </button>
            <button type="button" className="am-btn am-btn-outline" onClick={marcarApenasObrigatorios}>
              <span className="ms">flag</span>
              Marcar apenas Obrigatórios
            </button>
            <button type="button" className="am-btn am-btn-outline" onClick={marcarCoreObrigatorios}>
              <span className="ms">military_tech</span>
              Marcar CORE Obrigatórios
            </button>
          </div>
        </div>
      </div>

      <div className="d-flex flex-column gap-2">
        {secoes.map((secao) => {
          const requisitosSecaoTotal = secao.subsecoes.flatMap(requisitosDaSubsecao);
          const requisitosSecaoFiltrados = requisitosSecaoTotal.filter(
            (r) => (filtroCore !== 'core' || r.core) && passaBusca(r)
          );
          if (requisitosSecaoFiltrados.length === 0) return null;

          const totalAtivos = requisitosSecaoFiltrados.filter((r) => ativoAtual(r.id_requisito)).length;
          const aberta = secoesAbertas.has(secao.id_secao) || buscaAtiva;

          const idsSecaoFiltrados = requisitosSecaoFiltrados.map((r) => r.id_requisito);

          return (
            <div key={secao.id_secao} className="card-soft" style={{ overflow: 'hidden' }}>
              <div className="d-flex align-items-center gap-3" style={{ padding: '16px 20px' }}>
                <CheckboxCascata
                  estado={estadoSelecao(idsSecaoFiltrados, ativoAtual)}
                  onClick={() => alternarBloco(idsSecaoFiltrados)}
                  title="Marcar/desmarcar todos os requisitos desta Seção"
                />
                <button
                  type="button"
                  className="d-flex align-items-center gap-2 flex-grow-1 border-0 bg-transparent text-start"
                  style={{ padding: 0, cursor: 'pointer' }}
                  onClick={() => toggleSecao(secao.id_secao)}
                >
                  <span className="ms" style={{ fontSize: 20, color: 'var(--am-cinza-texto-2)' }}>
                    {aberta ? 'expand_more' : 'chevron_right'}
                  </span>
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--am-tinta)' }}>
                      Seção {secao.numero} — {secao.nome}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--am-cinza-texto-2)' }}>
                      {totalAtivos} de {requisitosSecaoFiltrados.length} requisito(s)
                      {filtroCore === 'core' ? ' CORE' : ''} ativos
                    </div>
                  </div>
                </button>
              </div>

              {aberta && (
                <div style={{ borderTop: '1px solid var(--am-borda-2)' }}>
                  {secao.subsecoes.map((sub) => {
                    const requisitos = requisitosDaSubsecao(sub).filter(
                      (r) => (filtroCore !== 'core' || r.core) && passaBusca(r)
                    );
                    if (requisitos.length === 0) return null;
                    const idsSubFiltrados = requisitos.map((r) => r.id_requisito);
                    return (
                      <div key={sub.id_subsecao} style={{ padding: '13px 20px 13px 44px', borderBottom: '1px solid var(--am-borda-2)' }}>
                        <div className="d-flex align-items-center gap-2" style={{ marginBottom: 8 }}>
                          <CheckboxCascata
                            estado={estadoSelecao(idsSubFiltrados, ativoAtual)}
                            onClick={() => alternarBloco(idsSubFiltrados)}
                            title="Marcar/desmarcar todos os requisitos desta Subseção"
                          />
                          <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--am-tinta)' }}>
                            {sub.numero} — {sub.nome}
                          </span>
                          <Pill tone={HOSPITALAR_TONE[sub.hospitalar] || 'neutral'}>
                            {HOSPITALAR_LABELS[sub.hospitalar] || sub.hospitalar}
                          </Pill>
                        </div>
                        <div className="d-flex flex-wrap gap-2">
                          {requisitos.map((r) => {
                            const ativo = ativoAtual(r.id_requisito);
                            return (
                              <button
                                type="button"
                                key={r.id_requisito}
                                onClick={() => toggleRequisito(r.id_requisito)}
                                className="am-checkbox"
                                title={r.core ? `${r.texto} (requisito CORE)` : r.texto}
                                style={{
                                  padding: '6px 10px',
                                  borderRadius: 9,
                                  background: ativo ? 'var(--am-amarelo-nevoa)' : '#f4f3ee',
                                  border: r.core ? '1.5px solid var(--am-link)' : '1.5px solid transparent',
                                }}
                              >
                                <span className={`am-checkbox__box${ativo ? ' is-checked' : ''}`}>
                                  {ativo && <span className="ms">check</span>}
                                </span>
                                <span
                                  style={{
                                    fontWeight: r.core ? 700 : 400,
                                    color: r.core ? 'var(--am-link)' : 'inherit',
                                  }}
                                >
                                  {r.codigo}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
